/**
 * Calculatie & Kostprijs-module — gedeelde types: enums, engine-I/O,
 * API-DTO's en Zod-schemas. Batch-4 calc, spec `calculatie-kostprijs-module`.
 *
 * De pure rekenlogica leeft in `@streep/utils` (`calculation-engine.ts`); deze
 * module levert de I/O-types + de API-DTO's. Kostprijsopbouw + salarisinputs zijn
 * serverside afgeschermd (de DTO maskeert de opbouw-velden naar `null` voor
 * viewers zonder recht) + geaudit.
 */

import { z } from 'zod'
import type { EmployeeCostBreakdownDTO } from './employee-cost'

// ─── Enums (gespiegeld uit de Prisma-enums; los gehouden van @prisma/client) ──

export const CALCULATION_TYPES = [
  'OFFERTE',
  'PROJECT',
  'ONDERHOUD',
  'DETACHERING',
  'STORINGSDIENST',
  'PLANNING',
  'HISTORISCH',
  'SCENARIO',
  'HANDMATIG',
] as const
export type CalculationType = (typeof CALCULATION_TYPES)[number]

export const CALCULATION_SOURCES = [
  'LEEG',
  'OFFERTE',
  'PLANNING',
  'HISTORIE',
  'PROJECT',
  'RELATIE',
  'MEDEWERKER',
  'WERKBON',
] as const
export type CalculationSource = (typeof CALCULATION_SOURCES)[number]

export const CALCULATION_STATUSES = [
  'CONCEPT',
  'IN_CONTROLE',
  'GOEDGEKEURD_INTERN',
  'OMGEZET_NAAR_OFFERTE',
  'GEKOPPELD_AAN_PLANNING',
  'GEREALISEERD',
  'AFGESLOTEN',
  'GEANNULEERD',
] as const
export type CalculationStatus = (typeof CALCULATION_STATUSES)[number]

export const CALCULATION_LINE_TYPES = [
  'ARBEID',
  'REIS',
  'MATERIAAL',
  'ONDERAANNEMING',
  'TOESLAG',
  'OVERIG',
] as const
export type CalculationLineType = (typeof CALCULATION_LINE_TYPES)[number]

export const CALCULATION_PARAMETER_SET_SCOPES = ['JAAR', 'KLANT', 'SCENARIO', 'GLOBAAL'] as const
export type CalculationParameterSetScope = (typeof CALCULATION_PARAMETER_SET_SCOPES)[number]

// ─── Engine-I/O (pure rekenlogica in @streep/utils/calculation-engine) ───────

/// Invoer voor `computeCalculationLine`. Alle bedragen/uren als number; de DB-
/// laag converteert number <-> Prisma Decimal op de rand.
export interface CalculationLineComputeInput {
  lineType: CalculationLineType
  hours: number
  travelHours: number
  kilometers: number
  costRate: number // €/uur kostprijs (ARBEID/TOESLAG)
  salesRate: number // €/uur verkoop (ARBEID/TOESLAG) of €/km (REIS)
  travelCostRate: number // €/reisuur kostprijs (REIS) — verloning
  travelSalesRate: number // €/reisuur verkoop (REIS) — facturatie
  payrollSurchargePct: number // toeslag verloning (verhoogt de kost)
  invoiceSurchargePct: number // toeslag facturatie (verhoogt de omzet)
  materialCost: number // inkoop materiaal (MATERIAAL)
  externalCost: number // inkoop onderaanneming (ONDERAANNEMING)
  marginPct: number // opslag inkoop -> verkoop (MATERIAAL/ONDERAANNEMING)
  // Voor OVERIG: directe omzet/kost (handmatig), genegeerd bij de andere types.
  revenue?: number
  cost?: number
}

export interface CalculationLineComputeResult {
  revenue: number
  cost: number
  margin: number
  marginPercentage: number
}

export interface CalculationTotals {
  totalRevenue: number
  totalCost: number
  totalMargin: number
  marginPercentage: number
}

/// Advies-verkooptarief: kostprijs / (1 - marge%). Bv. €46 + 25% => €61,33.
export interface AdviceRateInput {
  costRate: number
  gewensteMargePct: number
}
export interface AdviceRateResult {
  adviceRate: number
  margin: number
  marginPercentage: number
}

/// Scenario-aanpassingen (deel 10): speel met tarief/medewerker/reisuren/etc.
export interface ScenarioAanpassingen {
  tariefDeltaPct?: number // +/- % op alle verkooptarieven
  tariefDeltaBedrag?: number // +/- €/uur op alle verkooptarieven
  reisFacturabel?: boolean // false => reisomzet eruit
  klantkortingPct?: number // korting op de omzet
  extraOverwerkUren?: number // extra uren tegen overwerktoeslag
  overwerkToeslagPct?: number // toeslag voor de extra overwerkuren
  kostprijsDeltaPct?: number // +/- % op de kostprijs (andere/duurdere medewerker)
}

/// Een waarschuwing/advies bij een calculatie (deel 9).
/// FO-159: GEEN_KOSTPRIJSPROFIEL/GEEN_KLANTTARIEF melden ontbrekende brondata
/// (kostprijs uit `EmployeeCostProfile`, tarief uit `ClientTariff`) die anders
/// stil op 0 zou uitkomen — "ontbrekend" is iets anders dan "echt 0".
export const CALCULATION_WARNING_CODES = [
  'MARGE_ONDER_MINIMUM',
  'KOSTPRIJS_BOVEN_TARIEF',
  'REISUREN_KOSTEN_ZONDER_OMZET',
  'PLOEGTOESLAG_MIST',
  'FACTURABEL_UIT_BIJ_UREN',
  'GEEN_REGELS',
  'GEEN_KOSTPRIJSPROFIEL',
  'GEEN_KLANTTARIEF',
  'OMZET_ZONDER_KOSTEN',
] as const
export type CalculationWarningCode = (typeof CALCULATION_WARNING_CODES)[number]

export interface CalculationWarning {
  code: CalculationWarningCode
  severity: 'info' | 'warning' | 'error'
  message: string
  /// Directe aanvul-link (backoffice-route) om de ontbrekende brondata te
  /// herstellen, bv. `/medewerkers/<id>?tab=kostprijs` of
  /// `/relaties/<id>?tab=tarieven`. Alleen gezet op brondata-waarschuwingen.
  link?: string | null
}

// ─── API-DTO's ───────────────────────────────────────────────────────────────

/// Afscherming: voor viewers zonder `viewCostBreakdown` worden de opbouw-velden
/// (costRate/cost/margin/payrollSurchargePct) op `null` gezet (de DTO blijft
/// vorm-stabiel). Omzet + totalen blijven zichtbaar.
export interface CalculationLineDTO {
  id: string
  calculationId: string
  lineType: CalculationLineType
  position: number
  employeeId: string | null
  employeeNaam: string | null
  roleId: string | null
  description: string | null
  hours: number
  travelHours: number
  kilometers: number
  costRate: number | null
  salesRate: number
  travelCostRate: number | null
  travelSalesRate: number
  payrollSurchargePct: number | null
  invoiceSurchargePct: number
  materialCost: number | null
  externalCost: number | null
  /// null = geredigeerd: uit marge-opslag + omzet is de inkoopprijs triviaal te
  /// herleiden (revenue ÷ (1 + marginPct/100)), dus dit veld gaat mee dicht
  /// voor viewers zonder breakdown-recht (FO-159 review m2).
  marginPct: number | null
  revenue: number
  cost: number | null
  margin: number | null
  marginPercentage: number
  handmatigAangepast: boolean
  afwijkingReden: string | null
}

export interface CalculationScenarioDTO {
  id: string
  calculationId: string
  naam: string
  type: string
  aanpassingen: ScenarioAanpassingen | null
  totalRevenue: number
  totalCost: number | null
  totalMargin: number | null
  marginPercentage: number
  createdAt: string
}

export interface CalculationAuditDTO {
  id: string
  actie: string
  actor: string
  actorNaam: string | null
  reden: string | null
  createdAt: string
}

export interface CalculationDTO {
  id: string
  type: CalculationType
  sourceType: CalculationSource
  sourceId: string | null
  titel: string | null
  clientId: string | null
  clientNaam: string | null
  projectName: string | null
  bvId: string | null
  bvNaam: string | null
  status: CalculationStatus
  parameterSetId: string | null
  parameterSetNaam: string | null
  voorcalculatieId: string | null
  isNacalculatie: boolean
  totalRevenue: number
  totalCost: number | null
  totalMargin: number | null
  marginPercentage: number
  minimumMargePctSnapshot: number | null
  gewensteMargePct: number | null
  periodeVan: string | null
  periodeTot: string | null
  geldigTot: string | null
  notes: string | null
  omgezetNaarQuoteId: string | null
  createdById: string
  createdByNaam: string | null
  approvedById: string | null
  approvedByNaam: string | null
  approvedAt: string | null
  createdAt: string
  updatedAt: string
  regels: CalculationLineDTO[]
  scenarios: CalculationScenarioDTO[]
  warnings: CalculationWarning[]
  // Afscherming: of de viewer de opbouw/salaris mag zien (serverside bepaald).
  canViewCostBreakdown: boolean
  canApprove: boolean
}

export interface CalculationListItemDTO {
  id: string
  type: CalculationType
  sourceType: CalculationSource
  titel: string | null
  clientNaam: string | null
  bvNaam: string | null
  status: CalculationStatus
  totalRevenue: number
  totalCost: number | null
  totalMargin: number | null
  marginPercentage: number
  minimumMargePctSnapshot: number | null
  isNacalculatie: boolean
  periodeVan: string | null
  periodeTot: string | null
  createdAt: string
}

export interface CalculationParameterSetDTO {
  id: string
  naam: string
  scope: CalculationParameterSetScope
  jaar: number | null
  clientId: string | null
  clientNaam: string | null
  validFrom: string
  validUntil: string | null
  isActief: boolean
  values: Record<string, number>
  createdAt: string
  updatedAt: string
}

// ─── Kostenopbouw + tarief-herkomst per calculatie (FO-159) ──────────────────

/// Live kostenopbouw van één medewerker op de calculatie: de employee-cost-
/// componenten achter Kost/u. Alleen geleverd aan breakdown-rollen; de
/// salaris-herleidbare velden binnen `componenten` kunnen daarbovenop nog
/// gemaskeerd zijn (`EmployeeCostBreakdownDTO`, salaris-redactie).
export interface CalculationEmployeeCostBreakdownDTO {
  employeeId: string
  employeeNaam: string | null
  /// Huidige integrale kostprijs/uur uit het profiel (live berekend);
  /// null = onbekend (geen profiel/declarabele uren).
  kostprijsPerUur: number | null
  fteFactor: number
  declarabeleUren: number
  /// Jaar-componenten (loon, lasten, overhead, voertuig, bijkomend, ...);
  /// null wanneer er geen kostprijsprofiel te berekenen is.
  componenten: EmployeeCostBreakdownDTO | null
  /// True = geen bruikbaar kostprijsprofiel -> kostprijs onbekend (niet €0).
  profielOntbreekt: boolean
}

/// Herkomst van Verkoop/u: welk `ClientTariff` de verkooptarieven levert.
export interface CalculationTariffHerkomstDTO {
  /// False = er bestaat géén ClientTariff voor deze klant ("ontbrekend").
  heeftTarief: boolean
  tariffId: string | null
  tariffNaam: string | null
  isDefault: boolean
  hourlyRate: number
  overtimeRate: number
  travelRatePerKm: number
  reisUurtarief: number
  shiftSurchargePct: number
}

export interface CalculationCostBreakdownDTO {
  calculationId: string
  clientId: string | null
  clientNaam: string | null
  employees: CalculationEmployeeCostBreakdownDTO[]
  /// null = geen klant aan de calculatie gekoppeld.
  tarief: CalculationTariffHerkomstDTO | null
}

/// Preview-regel (maken-scherm, FO-159): de gebouwde regel + berekende
/// bedragen. Kost-kant is `null` voor viewers zonder breakdown-recht
/// (zelfde redactie als `CalculationLineDTO`).
export interface CalculationPreviewLineDTO {
  lineType: CalculationLineType
  employeeId: string | null
  employeeNaam: string | null
  description: string | null
  hours: number
  travelHours: number
  kilometers: number
  salesRate: number
  travelSalesRate: number
  costRate: number | null
  travelCostRate: number | null
  revenue: number
  cost: number | null
  margin: number | null
  marginPercentage: number
}

// ─── Zod-schemas (gedeeld API + form, conventie validatie-afscherming) ───────

export const calculationLineInputSchema = z.object({
  id: z.string().optional(),
  lineType: z.enum(CALCULATION_LINE_TYPES),
  position: z.number().int().min(0).optional(),
  employeeId: z.string().nullable().optional(),
  roleId: z.string().nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  hours: z.number().min(0).default(0),
  travelHours: z.number().min(0).default(0),
  kilometers: z.number().int().min(0).default(0),
  costRate: z.number().min(0).default(0),
  salesRate: z.number().min(0).default(0),
  travelCostRate: z.number().min(0).default(0),
  travelSalesRate: z.number().min(0).default(0),
  payrollSurchargePct: z.number().min(0).default(0),
  invoiceSurchargePct: z.number().min(0).default(0),
  materialCost: z.number().min(0).default(0),
  externalCost: z.number().min(0).default(0),
  marginPct: z.number().min(0).default(0),
  // Alleen voor OVERIG: directe omzet/kost (de engine negeert ze bij de andere
  // regeltypes). FO-159: zonder deze velden kon een OVERIG-regel (bv. de
  // offerte-werk-regel) nooit omzet krijgen - een stil-0-pad.
  revenue: z.number().min(0).optional(),
  cost: z.number().min(0).optional(),
  handmatigAangepast: z.boolean().optional(),
  afwijkingReden: z.string().max(500).nullable().optional(),
})
export type CalculationLineInput = z.infer<typeof calculationLineInputSchema>

export const createCalculationSchema = z.object({
  type: z.enum(CALCULATION_TYPES),
  sourceType: z.enum(CALCULATION_SOURCES).optional(),
  sourceId: z.string().nullable().optional(),
  titel: z.string().max(200).nullable().optional(),
  clientId: z.string().nullable().optional(),
  projectName: z.string().max(200).nullable().optional(),
  bvId: z.string().nullable().optional(),
  parameterSetId: z.string().nullable().optional(),
  gewensteMargePct: z.number().min(0).max(100).nullable().optional(),
  periodeVan: z.coerce.date().nullable().optional(),
  periodeTot: z.coerce.date().nullable().optional(),
  geldigTot: z.coerce.date().nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  regels: z.array(calculationLineInputSchema).optional(),
})
export type CreateCalculationInput = z.infer<typeof createCalculationSchema>

export const updateCalculationSchema = z.object({
  titel: z.string().max(200).nullable().optional(),
  clientId: z.string().nullable().optional(),
  projectName: z.string().max(200).nullable().optional(),
  bvId: z.string().nullable().optional(),
  parameterSetId: z.string().nullable().optional(),
  gewensteMargePct: z.number().min(0).max(100).nullable().optional(),
  periodeVan: z.coerce.date().nullable().optional(),
  periodeTot: z.coerce.date().nullable().optional(),
  geldigTot: z.coerce.date().nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  regels: z.array(calculationLineInputSchema).optional(),
})
export type UpdateCalculationInput = z.infer<typeof updateCalculationSchema>

/// Calculatie maken vanuit een bron (deel 5/6/7/8/11). De relevante id's hangen
/// af van de bron; de service negeert niet-toepasselijke velden.
export const createFromSourceSchema = z.object({
  source: z.enum(CALCULATION_SOURCES),
  type: z.enum(CALCULATION_TYPES).optional(),
  titel: z.string().max(200).nullable().optional(),
  clientId: z.string().nullable().optional(),
  bvId: z.string().nullable().optional(),
  quoteId: z.string().nullable().optional(),
  projectId: z.string().nullable().optional(),
  werkbonIds: z.array(z.string()).optional(),
  employeeIds: z.array(z.string()).optional(),
  voorcalculatieId: z.string().nullable().optional(),
  isNacalculatie: z.boolean().optional(),
  periodeVan: z.coerce.date().nullable().optional(),
  periodeTot: z.coerce.date().nullable().optional(),
  gewensteMargePct: z.number().min(0).max(100).nullable().optional(),
})
export type CreateFromSourceInput = z.infer<typeof createFromSourceSchema>

export const adviceRateInputSchema = z.object({
  costRate: z.number().min(0),
  gewensteMargePct: z.number().min(0).max(99.999),
})

export const createScenarioSchema = z.object({
  naam: z.string().min(1).max(100),
  type: z.string().max(50).default('Handmatig'),
  aanpassingen: z
    .object({
      tariefDeltaPct: z.number().optional(),
      tariefDeltaBedrag: z.number().optional(),
      reisFacturabel: z.boolean().optional(),
      klantkortingPct: z.number().min(0).max(100).optional(),
      extraOverwerkUren: z.number().min(0).optional(),
      overwerkToeslagPct: z.number().min(0).optional(),
      kostprijsDeltaPct: z.number().optional(),
    })
    .optional(),
})
export type CreateScenarioInput = z.infer<typeof createScenarioSchema>

export const approveCalculationSchema = z.object({
  approveBelowMargin: z.boolean().optional(),
  reden: z.string().max(500).nullable().optional(),
})
export type ApproveCalculationInput = z.infer<typeof approveCalculationSchema>

export const updateCalculationStatusSchema = z.object({
  status: z.enum(CALCULATION_STATUSES),
  reden: z.string().max(500).nullable().optional(),
})

export const createParameterSetSchema = z.object({
  naam: z.string().min(1).max(120),
  scope: z.enum(CALCULATION_PARAMETER_SET_SCOPES).default('JAAR'),
  jaar: z.number().int().min(2000).max(2100).nullable().optional(),
  clientId: z.string().nullable().optional(),
  validFrom: z.coerce.date().optional(),
  validUntil: z.coerce.date().nullable().optional(),
  values: z.record(z.string(), z.number()),
})
export type CreateParameterSetInput = z.infer<typeof createParameterSetSchema>

export const updateParameterSetSchema = z.object({
  naam: z.string().min(1).max(120).optional(),
  jaar: z.number().int().min(2000).max(2100).nullable().optional(),
  clientId: z.string().nullable().optional(),
  validFrom: z.coerce.date().optional(),
  validUntil: z.coerce.date().nullable().optional(),
  isActief: z.boolean().optional(),
  values: z.record(z.string(), z.number()).optional(),
})
export type UpdateParameterSetInput = z.infer<typeof updateParameterSetSchema>

/// Minimum-marge per klant (deel 9) — PATCH op de relatie/CRM-tab.
export const updateClientMinimumMargeSchema = z.object({
  minimumMargePct: z.number().min(0).max(100).nullable(),
})
export type UpdateClientMinimumMargeInput = z.infer<typeof updateClientMinimumMargeSchema>
